import { Box } from '@mui/system';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Button, Stack, Tab, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Editor } from '../../../../../components/wysiwyg/Editor';
import { OpportunitiesList } from '../../../../../actions/OpportunitiesList';
import { Add } from '@mui/icons-material';
import { useModalContext } from '../../../../../modal/ModalContext';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC, useEffect, useState } from 'react';
import { graphql } from '../../../../../gql';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useDebounceEffect } from '../../../../../utils/useDebounceEffect';
import { useSnackbar } from '../../../../../store/snackbar';

const actionDrawerTabs_getAction = graphql(`
	query actionDrawerTabs_getAction($actionId: UUID!) {
		getAction(by: { id: $actionId }) {
			description
			opportunities {
				id
			}
		}
	}
`);

const actionDrawerTabs_updateAction = graphql(`
	mutation actionDrawerTabs_updateAction($actionId: UUID!, $data: ActionUpdateInput!) {
		updateAction(by: { id: $actionId }, data: $data) {
			ok
			errorMessage
		}
	}
`);

// NOTE: Create multiple instances of the same mutation to separate loading states of input components.
const useUpdateAction = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	return useApiMutation(actionDrawerTabs_updateAction, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateAction.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'action.detail.update.success' }),
					error: intl.formatMessage({ id: 'action.detail.update.error' }),
				},
				errorMessage: result.updateAction.errorMessage,
				openSnackbar,
			}),
	});
};

type ActionDrawerTabsProps = Readonly<{
	actionId: string;
	activityId?: string;
}>;

export const ActionDrawerTabs: FC<ActionDrawerTabsProps> = ({ actionId, activityId }) => {
	const intl = useIntl();
	const theme = useTheme();
	const { setModal } = useModalContext();
	const [description, setDescription] = useState('');
	const [isEditorReady, setIsEditorReady] = useState(false);

	const { data: action, isPending: isActionPending } = useApiQuery(actionDrawerTabs_getAction, {
		variables: { actionId },
	});

	const { mutate: updateDescription } = useUpdateAction();

	useEffect(() => {
		if (!isActionPending) {
			setDescription(action?.getAction?.description ?? '');
			setIsEditorReady(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isActionPending]);

	useDebounceEffect(() => {
		if (isEditorReady && action?.getAction?.description !== description) {
			updateDescription({ actionId, data: { description } });
		}
	}, [isEditorReady, action, description]);

	const [tab, setTab] = useState('1');

	const openAddOpportunityModal = () => {
		setModal({ type: 'addOpportunity', payload: { actionId, activityId } });
	};

	const getOpportunitiesCount = () => {
		const count = action?.getAction?.opportunities.length;
		if (count != null && count > 0) {
			return `(${count})`;
		}
		return '';
	};

	return (
		<TabContext value={tab}>
			<Box px={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<TabList onChange={(_, value) => setTab(value)}>
					<Tab
						label={intl.formatMessage({ id: 'journey.detail.widget.actions.tabs.details' })}
						value={'1'}
						sx={{ fontSize: 16, lineHeight: 1.5 }}
					></Tab>
					<Tab
						label={intl.formatMessage({ id: 'journey.detail.widget.actions.tabs.opportunities' }, { count: getOpportunitiesCount() })}
						value={'2'}
						sx={{ fontSize: 16, lineHeight: 1.5 }}
					></Tab>
				</TabList>
			</Box>
			<TabPanel value={'1'}>
				<Stack direction={'column'} gap={2.5}>
					<Typography variant={'h6'} color={theme.palette.grey[600]}>
						<FormattedMessage id={'journey.detail.widget.actions.description'} />
					</Typography>
					{isEditorReady && <Editor state={description} setState={setDescription} />}
				</Stack>
			</TabPanel>
			<TabPanel value={'2'}>
				<Stack direction={'column'} gap={2.5}>
					<OpportunitiesList actionId={actionId} size={'small'} />
					<Box alignSelf={'flex-start'}>
						<Button type={'button'} color={'secondary'} onClick={openAddOpportunityModal}>
							<Stack direction={'row'} gap={1} sx={{ color: theme.palette.secondary.main }} alignItems={'center'}>
								<Add sx={{ fontSize: 18 }} />
								<Typography variant={'h6'}>
									<FormattedMessage id={'journey.detail.widget.actions.add-opportunity'} />
								</Typography>
							</Stack>
						</Button>
					</Box>
				</Stack>
			</TabPanel>
		</TabContext>
	);
};
